import { useQuery } from '@tanstack/react-query'
import React, { useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import ReportTransactionsBoxAndLinks from 'src/components/ReportTransactionsBoxAndLinks/ReportTransactionsBoxAndLinks'
import { TransactionReportType } from 'src/components/ReportTransactionsBoxAndLinks/reportTypes'
import { TransactionsTableLargeDesigns } from 'src/components/TransactionsDataGrid/TransactionsDataGrid'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { TransactionFilterOptions } from 'src/enums'
import { useEventListener } from 'src/hooks'
import useClearCacheOnUnmount from 'src/hooks/queryCache.hooks'
import { CreditCardType } from 'src/models/creditCard.models'
import { useQueryCreditCards } from 'src/queries/creditCard.queries'
import { orgReceiptManagementQueries } from 'src/queries/organizations/receiptManagement/receiptManagement.queries'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

const AccountingTransactions: React.FC = () => {
  const user = useCurrentUser()
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )

  useClearCacheOnUnmount()

  const { results: statistics, anyLoading: reactQueryStatisticsLoading } =
    useQueryStatistics({
      statisticsKeys: [
        StatisticsKeys.CreditCardsSourcePhysicalClearingsLinkedCount,
      ],
    })
  const creditCardsSourcePhysicalClearingsLinkedCount =
    statistics?.CreditCardsSourcePhysicalClearingsLinkedCount?.data ?? 0

  const appName = useSelector(selectAppName)
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const { data: receiptManagementEnabled } = useQuery(
    orgReceiptManagementQueries.enabled(organization?.id),
  )

  const [initialLoad, setInitialLoad] = useState(true)

  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })
  const clientHeight = useMemo(
    () => viewportHeight.height - 122,
    [viewportHeight],
  )

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEventListener('resize', updateWindowSize)

  const { data: creditCardsData, isFetched: creditCardsFetched } =
    useQueryCreditCards({
      filters: {
        search: undefined,
        type: CreditCardType.Source,
        count: 2,
        sortField: 'type',
        sortDirection: 'ASC',
      },
    })

  return reactQueryStatisticsLoading || !creditCardsFetched ? (
    <LoadingBackgroundPage />
  ) : (
    <>
      <Helmet>
        <title>{`Review Reports | ${appName}`}</title>
        <meta
          name="description"
          content={`Review Reports page of ${appName}`}
        />
      </Helmet>
      <BackgroundPageContent noStackingIndex noPadding>
        <ReportTransactionsBoxAndLinks
          context={TransactionReportType.AccountingTransactions}
          showIntegrationsLink={true}
          showConnection
          design={TransactionsTableLargeDesigns.UserAndCard}
          initialLoad={initialLoad}
          setInitialLoad={setInitialLoad}
          visibleFilters={[
            TransactionFilterOptions.TransactionAmountRange,
            // using creditCardsSourcePhysicalClearingsLinkedCount > 0 check until BE updates Regions cards to accurately set physicalClearingsEnabled
            ...(organization?.physicalClearingsEnabled ||
            creditCardsSourcePhysicalClearingsLinkedCount > 0
              ? [TransactionFilterOptions.TransactionOrigin]
              : []),
            TransactionFilterOptions.ExpenseCategoryStatus,
            TransactionFilterOptions.ExpenseCategoryLabels,
            ...(receiptManagementEnabled !== undefined
              ? [TransactionFilterOptions.ReceiptStatus]
              : []),
            ...(activeIntegration !== undefined &&
            user?.hasOrgBookkeeperPermissions
              ? [
                  TransactionFilterOptions.TransactionSyncStatus,
                  TransactionFilterOptions.ReceiptSyncStatus,
                ]
              : []),
            TransactionFilterOptions.CardUser,
            TransactionFilterOptions.Departments,
            TransactionFilterOptions.Account,
            TransactionFilterOptions.MerchantCategory,
            TransactionFilterOptions.Search,
          ]}
          clientHeight={clientHeight || 0}
          initialFilters={{
            ...(creditCardsData?.creditCards?.length === 1 && {
              creditCardId: new Set([creditCardsData.creditCards[0].id]),
            }),
          }}
        />
      </BackgroundPageContent>
    </>
  )
}

export default AccountingTransactions
